import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Mui } from '@leap4/frontend';
import { urlService } from '../../routing/urlsService';

const { Box, Tabs, Tab } = Mui;

/**
 * The two top-level views, as a tab bar.
 *
 * Rendered on the list screens *and* inside a publisher, so switching between publishers and
 * lines is always one click. Without it, getting from a publisher to the lines view meant going
 * back first - which reads as a hierarchy, and they aren't one: publishers and lines are two
 * views of the same data.
 *
 * Driven by the route rather than local state, so /app-ads-lines/all stays a shareable link and
 * the browser's back button behaves.
 *
 * `action` is the primary check for whatever you're looking at - "Update all" on the list
 * screens, "Check now" on a single publisher. Keeping it in one fixed place means the button
 * you reach for doesn't move as you navigate.
 */
export const AppAdsLinesTabs: React.FC<{ action?: React.ReactNode }> = ({ action }) => {
    const history = useHistory();
    const location = useLocation();

    const onLines = location.pathname.startsWith(urlService.appAdsLinesAll());

    return (
        <Box display="flex" justifyContent="space-between" alignItems="flex-end" flexWrap="wrap" gap={1}>
            <Tabs
                value={onLines ? 1 : 0}
                onChange={(_, v) => history.push(v === 1 ? urlService.appAdsLinesAll() : urlService.appAdsLines())}
            >
                {/* A publisher's own page counts as "publishers" - it's a view of one of them. */}
                <Tab label="All publishers" />
                <Tab label="All lines" />
            </Tabs>

            {action && <Box sx={{ mb: 1 }}>{action}</Box>}
        </Box>
    );
};

export default AppAdsLinesTabs;
